import type { PropType } from 'vue';
import type { TCheckboxProps } from './TCheckbox.types';
import { merge } from 'es-toolkit/compat';
import { computed, defineComponent, ref, watch } from 'vue';
import { useStyles } from './TCheckbox.styles';

const generateUniqueId = () => `checkbox-${
  Math.random()
    .toString(36)
    .substring(2, 9)
}-${Date.now()}`;

const defaultProps = {
  size: 'large',
  value: false,
};

export const TCheckbox = defineComponent({
  name: 'TCheckbox',
  props: {
    labelPosition: String as PropType<TCheckboxProps['labelPosition']>,
    size: String as PropType<TCheckboxProps['size']>,
    value: Boolean as PropType<TCheckboxProps['value']>,
    modelValue: Boolean as PropType<TCheckboxProps['modelValue']>,
    disabled: Boolean as PropType<TCheckboxProps['disabled']>,
    id: String as PropType<TCheckboxProps['id']>,
  },
  emits: ['input', 'change', 'update:modelValue'],
  setup(_props, { attrs, slots, emit }) {
    const localId = generateUniqueId();

    const props = computed(() => merge({}, defaultProps, _props, attrs, {
      id: _props.id ?? (attrs as any).id ?? localId,
    }));

    const baseClass = useStyles();
    const classes = computed(() => [
      baseClass.value,
      `${baseClass.value}--${props.value.size}`,
    ]);
    const textClass = computed(() => [
      baseClass.value,
      `${baseClass.value}--${props.value.size}`,
      { [`${baseClass.value}--disabled`]: props.value.disabled },
    ]);

    const isChecked = ref(props.value.modelValue || props.value.value);
    watch([() => props.value.modelValue, () => props.value.value], () => {
      isChecked.value = props.value.modelValue || props.value.value;
    });

    const handleClick = () => {
      emit('update:modelValue', !isChecked.value);
      emit('input', !isChecked.value);
      emit('change', !isChecked.value);
      isChecked.value = !isChecked.value;
    };
    return () => (
      <div class={classes.value.map((class_) => `${class_}__wrapper`)}>
        {slots.default?.() && props.value.labelPosition === 'left' && (
          <label for={props.value.id} class={textClass.value}>
            {slots.default()}
          </label>
        )}
        <input
          id={props.value.id}
          type="checkbox"
          class={classes.value.map((class_) => `${class_}__input`)}
          checked={isChecked.value}
          disabled={props.value.disabled}
          onClick={handleClick}
          {...attrs}
        />
        {slots.default?.() && props.value.labelPosition === 'right' && (
          <label for={props.value.id} class={textClass.value}>
            {slots.default()}
          </label>
        )}
      </div>
    );
  },
});
